import { LOADER_WORKER_URL } from "./loaderWorkerUrl";

/*
 * The one worker that draws every loading screen on the page (public/loader-worker.v1.js; scripts/make-loader-worker.py says why it is ONE, and long-lived).
 *
 * Measured 2026-10-17: a worker cannot START while the main thread is busy. It needs a few milliseconds of idle main thread, and one created in the same task as a long stall waited out the
 * whole stall (1.2s at 4x slower CPU) before it drew its first frame. A loader that creates its worker when it appears is therefore starved exactly when the app is busy. So the worker is
 * started once, early (by the HTML's boot loader on the dashboard's address, otherwise as soon as the app starts), and every loader after that only ATTACHES a canvas to it: a message, with
 * nothing more needed from the main thread.
 */
export interface SurfaceHandlers {
  /** the worker has drawn its first picture on this canvas */
  onDrawing?: () => void;
  /** the worker cannot be used (it failed to load or run): the caller draws on the main thread instead */
  onFailed: () => void;
}

type PageWindow = Window & { __loaderWorker?: Worker };

const attached = new Map<string, SurfaceHandlers>();
let worker: Worker | null | undefined;   // undefined: not tried yet; null: this browser cannot, or the worker failed
let sequence = 0;

export const workersSupported = (): boolean =>
  typeof Worker !== "undefined" &&
  typeof OffscreenCanvas !== "undefined" &&
  typeof HTMLCanvasElement !== "undefined" &&
  typeof HTMLCanvasElement.prototype.transferControlToOffscreen === "function";

const giveUp = () => {
  const dead = worker;
  worker = null;
  (window as PageWindow).__loaderWorker = undefined;
  try {
    dead?.terminate();
  } catch (e) {
    /* already gone */
  }
  const waiting = Array.from(attached.values());
  attached.clear();
  waiting.forEach((h) => h.onFailed());
};

const create = (): Worker | null => {
  if (!workersSupported()) return null;
  let w: Worker;
  try {
    w = (window as PageWindow).__loaderWorker ?? new Worker(LOADER_WORKER_URL);   // the HTML's boot loader may have started it already
  } catch (e) {
    return null;
  }
  (window as PageWindow).__loaderWorker = w;
  w.addEventListener("message", (event: MessageEvent) => {
    const m = event.data;
    if (m && m.type === "drawing") attached.get(m.id)?.onDrawing?.();
  });
  w.addEventListener("error", giveUp);

  return w;
};

/** The page's loader worker, started now if it has not been (null where workers or OffscreenCanvas do not exist, or after the worker failed). */
export const getLoaderWorker = (): Worker | null => {
  if (worker === undefined) worker = create();

  return worker;
};

/**
 * Attaches a canvas to the worker. Returns how to detach it, or null when it cannot be (the caller draws on the main thread). The canvas can be handed over once, so each attach needs a
 * canvas of its own.
 */
export const attachSurface = (canvas: HTMLCanvasElement, dpr: number, reducedMotion: boolean, handlers: SurfaceHandlers): (() => void) | null => {
  const w = getLoaderWorker();
  if (!w) return null;
  let offscreen: OffscreenCanvas;
  try {
    offscreen = canvas.transferControlToOffscreen();
  } catch (e) {
    return null;
  }
  const id = `app-${++sequence}`;
  attached.set(id, handlers);
  const rect = canvas.getBoundingClientRect();
  w.postMessage({ type: "init", id, canvas: offscreen, width: rect.width, height: rect.height, dpr, reduced: reducedMotion }, [offscreen]);
  const observer =
    typeof ResizeObserver !== "undefined"
      ? new ResizeObserver((entries) => {
          const box = entries[0]?.contentRect;
          if (box && box.width > 0 && box.height > 0) w.postMessage({ type: "resize", id, width: box.width, height: box.height, dpr });
        })
      : null;
  observer?.observe(canvas);

  return () => {
    observer?.disconnect();
    attached.delete(id);
    w.postMessage({ type: "stop", id });
  };
};
